/**
 * Deal Models library: every model this account can see, filtered by search,
 * ownership and state through the URL (`/deal-models?q&ownership&status`).
 * Opening a card goes to its Editor; Clone copies a ready built-in into a
 * private model (one idempotent intent per model, so a retried click cannot
 * create two copies); New Deal Model creates a GoRules/ZEN DSL source.
 *
 * Gated by the `workspace.deal_models` component grant; creating and cloning
 * also need the `deal_model.custom.create` capability.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { IconCopy, IconPlus, IconSearch } from '@tabler/icons-react'
import { useAccess } from '../../api/queries'
import { useCloneDealModel, useCreateDealModel, useDealModelList } from '../../api/dealModels'
import type { DealModelsSearch } from '../../app/router'
import { Shell } from '../../app/Shell'
import { useListMotion } from '../../theme/motion'
import { Alert, Button, Dialog, Empty, Field, PickerRow, Skeletons, Textarea, TextInput } from '../../ui/kit'
import { NotFoundPage } from '../stubs'
import { createDealModelSource } from './dealModelSource'
import { ModelStatePill, shortIdentity } from './WorkspaceHead'

type Ownership = NonNullable<DealModelsSearch['ownership']>
type Status = NonNullable<DealModelsSearch['status']>

const OWNERSHIP_OPTIONS: Array<{ value: Ownership; label: string }> = [
  { value: 'all', label: 'All visible models' },
  { value: 'system', label: 'Built-in models' },
  { value: 'mine', label: 'My models' },
]
const STATUS_OPTIONS: Array<{ value: Status; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'ready', label: 'Ready' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'invalid', label: 'Invalid' },
]
const FAMILY_OPTIONS = [
  { value: 'custom', label: 'Custom' },
  { value: 'flip', label: 'Flip' },
  { value: 'btl', label: 'Buy to let' },
  { value: 'brr', label: 'Buy, refurbish, refinance' },
]

function modelKeyStem(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part, index) => index === 0 ? part.toLowerCase() : `${part[0]?.toUpperCase() ?? ''}${part.slice(1).toLowerCase()}`)
    .join('')
    .slice(0, 60) || 'customModel'
}

export function DealModelsPage() {
  const navigate = useNavigate({ from: '/deal-models' })
  const search = useSearch({ from: '/deal-models' })
  const access = useAccess()
  const allowed = access.can('workspace.deal_models')
  const models = useDealModelList(allowed)
  const canCreateCustomModel = access.capability('deal_model.custom.create')
  const cloneModel = useCloneDealModel()
  const createModel = useCreateDealModel()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [family, setFamily] = useState('custom')
  const cloneIntents = useRef(new Map<string, string>())
  const [query, setQuery] = useState(search.q ?? '')
  const ownership: Ownership = search.ownership ?? 'all'
  const status: Status = search.status ?? 'all'
  const [gridRef] = useListMotion<HTMLDivElement>()

  const setLibrarySearch = (patch: Partial<DealModelsSearch>) => void navigate({ search: (current) => ({ ...current, ...patch }), replace: true })
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = query.trim() || undefined
      if (q !== search.q) void navigate({ search: (current) => ({ ...current, q }), replace: true })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [query, search.q, navigate])
  useEffect(() => {
    // Preserve the in-progress whitespace when the debounced URL catches up.
    setQuery(current => current.trim() === (search.q ?? '') ? current : search.q ?? '')
  }, [search.q])

  const cloneIntent = (modelId: string) => {
    const existing = cloneIntents.current.get(modelId)
    if (existing) return existing
    const created = crypto.randomUUID()
    cloneIntents.current.set(modelId, created)
    return created
  }
  const visibleModels = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return (models.data?.items ?? []).filter((model) => {
      if (ownership === 'system' && model.sourceKind !== 'system') return false
      if (ownership === 'mine' && model.sourceKind !== 'user') return false
      if (status !== 'all' && model.state !== status) return false
      return !needle || [model.name, model.description, model.owner, model.state].some((value) => value.toLowerCase().includes(needle))
    })
  }, [models.data, ownership, query, status])

  if (access.isError) return <Shell head={<div className="toolbar"><div className="tt"><h1 className="t-page">Deal Models</h1></div></div>} status={<span>Deal Models</span>}><Alert title="Workspace access unavailable">{access.error.message}</Alert></Shell>
  if (!access.isPending && !allowed) return <NotFoundPage />

  const create = async () => {
    if (!canCreateCustomModel) return
    const trimmedName = name.trim()
    if (!trimmedName) return
    const logicalModelId = crypto.randomUUID()
    const modelKey = `${modelKeyStem(trimmedName)}${logicalModelId.replaceAll('-', '').slice(0, 8)}`
    const sourceDocument = createDealModelSource({ logicalModelId, modelKey, family, name: trimmedName, description: description.trim() })
    const created = await createModel.mutateAsync({ logicalModelId, modelKey, family, name: trimmedName, description: description.trim(), sourceDocument })
    setCreating(false)
    setName('')
    setDescription('')
    setFamily('custom')
    await navigate({ to: '/deal-models/$modelId/editor', params: { modelId: created.logicalModelId }, search: {} })
  }

  const total = models.data?.items.length
  const countLabel = total === undefined ? '…' : visibleModels.length === total ? `${total} ${total === 1 ? 'model' : 'models'}` : `${visibleModels.length} of ${total}`

  return (
    <Shell
      head={
        <div className="toolbar">
          <div className="tt">
            <h1 className="t-page">Deal Models</h1>
            <span className="pill is-muted">{countLabel}</span>
          </div>
          <div className="ta">
            <label className="tool-field dm-search">
              <IconSearch className="ic" aria-hidden="true" />
              <input aria-label="Search Deal Models" placeholder="Search models" value={query} maxLength={200} onChange={(event) => setQuery(event.target.value)} />
            </label>
            <PickerRow variant="tool" label="Show" options={OWNERSHIP_OPTIONS} value={ownership} defaultValue="all" clearable={false} onChange={(value) => setLibrarySearch({ ownership: value === 'all' || typeof value !== 'string' ? undefined : value as Ownership })} />
            <PickerRow variant="tool" label="State" options={STATUS_OPTIONS} value={status} defaultValue="all" clearable={false} onChange={(value) => setLibrarySearch({ status: value === 'all' || typeof value !== 'string' ? undefined : value as Status })} />
          </div>
          {canCreateCustomModel ? <div className="ta is-end"><Button kind="primary" onClick={() => setCreating(true)}><IconPlus className="ic-sm" />New Deal Model</Button></div> : null}
        </div>
      }
      status={<>
        <span>Finance model configuration</span>
        <span>Define typed inputs, GoRules/ZEN formulas and the outputs rendered by Deal Economics.</span>
      </>}
    >
      <Dialog
        open={creating && canCreateCustomModel}
        onClose={() => setCreating(false)}
        title="Create a Deal Model"
        size="lg"
        actions={<>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button kind="primary" busy={createModel.isPending} disabled={!name.trim()} onClick={() => { void create().catch(() => undefined) }}>Create DSL model</Button>
        </>}
      >
        <Alert info tight title="GoRules/ZEN model source">This creates a model with typed inputs, a calculated output and Deal Economics presentation metadata. Rust does not calculate it.</Alert>
        <Field label="Model name" required htmlFor="dm-new-name">
          <TextInput id="dm-new-name" placeholder="My auction model" value={name} onChange={setName} required autoFocus />
        </Field>
        <div className="dm-dialog-pick">
          <PickerRow label="Model family" options={FAMILY_OPTIONS} value={family} defaultValue="custom" clearable={false} onChange={(value) => setFamily(typeof value === 'string' ? value : 'custom')} />
        </div>
        <Field label="Description" htmlFor="dm-new-description">
          <Textarea id="dm-new-description" placeholder="What this model decides and who should use it" value={description} onChange={setDescription} rows={3} />
        </Field>
        {createModel.isError ? <Alert tight title="Model was not created">{createModel.error.message}</Alert> : null}
      </Dialog>

      {access.isPending || models.isPending ? (
        <div className="list-grid dm-grid-cards" aria-label="Loading Deal Models"><Skeletons count={6} kind="block" /></div>
      ) : models.isError ? (
        <Alert title="Deal Models unavailable" action={<Button onClick={() => void models.refetch()}>Retry</Button>}>{models.error.message}</Alert>
      ) : visibleModels.length === 0 ? (
        <Empty
          title={models.data.items.length === 0 ? 'No Deal Models yet' : 'No visible models match'}
          action={models.data.items.length === 0 && canCreateCustomModel ? <Button kind="primary" onClick={() => setCreating(true)}><IconPlus className="ic-sm" />Create your first model</Button> : undefined}
        >
          {models.data.items.length === 0 ? canCreateCustomModel ? 'Create a model to define inputs and outputs.' : 'No models are available in your package.' : 'Clear the filters or ask a model owner to grant read/use visibility.'}
        </Empty>
      ) : (
        <div className="list-grid dm-grid-cards" ref={gridRef}>
          {visibleModels.map((model) => {
            const cloning = cloneModel.isPending && cloneModel.variables?.modelId === model.logicalModelId
            return (
              <article key={model.logicalModelId} className="list-card dm-model-card" data-testid={`deal-model-card-${model.logicalModelId}`}>
                <header className="dm-model-head">
                  <div>
                    <h2 className="n">{model.name}</h2>
                    <span className="d">{model.owner} · {model.visibility}</span>
                  </div>
                  <ModelStatePill state={model.state} />
                </header>
                <p className="dm-model-desc">{model.description}</p>
                <dl className="dm-identity">
                  <div><dt>Revision</dt><dd>{model.revision}</dd></div>
                  <div><dt>Source</dt><dd className="t-mono" title={model.sourceSha256}>{shortIdentity(model.sourceSha256)}</dd></div>
                </dl>
                <footer className="dm-model-foot">
                  <Button kind={model.capabilities.edit ? 'primary' : undefined} onClick={() => void navigate({ to: '/deal-models/$modelId/editor', params: { modelId: model.logicalModelId }, search: {} })}>
                    {model.capabilities.edit ? 'Open editor' : 'Inspect model'}
                  </Button>
                  {canCreateCustomModel && model.capabilities.use && model.state === 'ready' ? (
                    <Button
                      busy={cloning}
                      disabled={model.sourceKind !== 'system'}
                      title={model.sourceKind !== 'system' ? 'Only built-in models can be cloned here.' : undefined}
                      onClick={() => {
                        void cloneModel.mutateAsync({ modelId: model.logicalModelId, idempotencyKey: cloneIntent(model.logicalModelId) })
                          .then((cloned) => navigate({ to: '/deal-models/$modelId/editor', params: { modelId: cloned.logicalModelId }, search: {} }))
                          .catch(() => undefined)
                      }}
                    ><IconCopy className="ic-sm" />Clone</Button>
                  ) : null}
                </footer>
                {cloneModel.isError && cloneModel.variables?.modelId === model.logicalModelId ? <p className="dm-error">{cloneModel.error.message}</p> : null}
              </article>
            )
          })}
        </div>
      )}
    </Shell>
  )
}
